import { useState, useRef, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Play, Pause, Volume2, VolumeX, AlertCircle, Music, User, Clock, SkipBack, SkipForward } from 'lucide-react'
import { playlistShares, type SharedPlaylistInfo } from '../api/client'
import type { TrackWithDetails } from '../types'

function formatDuration(ms: number | null): string {
  if (!ms) return '--:--'
  const seconds = Math.floor(ms / 1000)
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export default function SharedPlaylist() {
  const { token } = useParams<{ token: string }>()
  const [currentTrackIndex, setCurrentTrackIndex] = useState<number | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(1)
  const [isMuted, setIsMuted] = useState(false)
  const audioRef = useRef<HTMLAudioElement>(null)

  const { data: shareInfo, isLoading, error } = useQuery<SharedPlaylistInfo>({
    queryKey: ['shared-playlist', token],
    queryFn: () => playlistShares.getSharedPlaylist(token!),
    enabled: !!token,
    retry: false,
  })

  const currentTrack = currentTrackIndex !== null && shareInfo
    ? shareInfo.playlist.tracks[currentTrackIndex]
    : null

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime)
    const handleLoadedMetadata = () => setDuration(audio.duration)
    const handleEnded = () => {
      // Auto-play next track
      if (shareInfo && currentTrackIndex !== null && currentTrackIndex < shareInfo.playlist.tracks.length - 1) {
        setCurrentTrackIndex(currentTrackIndex + 1)
      } else {
        setIsPlaying(false)
      }
    }

    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('ended', handleEnded)

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('ended', handleEnded)
    }
  }, [shareInfo, currentTrackIndex])

  // Auto-play when track changes
  useEffect(() => {
    const audio = audioRef.current
    if (audio && currentTrack && isPlaying) {
      audio.play().catch(() => {})
    }
  }, [currentTrackIndex, currentTrack])

  const playTrack = (index: number) => {
    if (currentTrackIndex === index) {
      togglePlayPause()
    } else {
      setCurrentTrackIndex(index)
      setIsPlaying(true)
    }
  }

  const togglePlayPause = () => {
    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      audio.pause()
    } else {
      if (currentTrackIndex === null && shareInfo && shareInfo.playlist.tracks.length > 0) {
        setCurrentTrackIndex(0)
      }
      audio.play().catch(() => {})
    }
    setIsPlaying(!isPlaying)
  }

  const playPrevious = () => {
    if (currentTrackIndex !== null && currentTrackIndex > 0) {
      setCurrentTrackIndex(currentTrackIndex - 1)
    }
  }

  const playNext = () => {
    if (shareInfo && currentTrackIndex !== null && currentTrackIndex < shareInfo.playlist.tracks.length - 1) {
      setCurrentTrackIndex(currentTrackIndex + 1)
    }
  }

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current
    if (!audio) return
    const time = parseFloat(e.target.value)
    audio.currentTime = time
    setCurrentTime(time)
  }

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current
    if (!audio) return
    const vol = parseFloat(e.target.value)
    audio.volume = vol
    setVolume(vol)
    setIsMuted(vol === 0)
  }

  const toggleMute = () => {
    const audio = audioRef.current
    if (!audio) return
    if (isMuted) {
      audio.volume = volume || 1
      setIsMuted(false)
    } else {
      audio.volume = 0
      setIsMuted(true)
    }
  }

  const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    )
  }

  if (error || !shareInfo) {
    const errorMessage = error instanceof Error ? error.message : 'This share link is no longer available'
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-semibold text-white mb-2">Share Not Available</h1>
          <p className="text-dark-400">{errorMessage}</p>
        </div>
      </div>
    )
  }

  const { playlist, shared_by_username, expires_at } = shareInfo
  const tracks = playlist.tracks

  // Get stream URL for a track in this shared playlist
  const getTrackStreamUrl = (track: TrackWithDetails) => {
    return `/api/stream/shared/playlist/${token}/${track.id}`
  }

  return (
    <div className="min-h-screen bg-dark-900 text-white">
      {/* Header */}
      <div className="bg-gradient-to-b from-dark-800 to-dark-900 p-6 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-end gap-6">
            <div className="w-40 h-40 md:w-56 md:h-56 bg-gradient-to-br from-primary-600 to-primary-800 rounded-lg flex items-center justify-center flex-shrink-0 shadow-xl">
              <Music size={64} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-dark-400 uppercase tracking-wider mb-2">Shared Playlist</p>
              <h1 className="text-2xl md:text-4xl font-bold mb-2 truncate">{playlist.name}</h1>
              <div className="flex items-center gap-2 text-dark-300 text-sm">
                <User size={14} />
                <span>Shared by {shared_by_username}</span>
                <span className="text-dark-500">•</span>
                <span>{tracks.length} songs</span>
              </div>
              {expires_at && (
                <p className="text-dark-500 text-xs mt-2">
                  Expires: {new Date(expires_at).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Track list */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {tracks.length > 0 ? (
          <div className="bg-dark-800 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="text-left text-dark-400 text-sm border-b border-dark-700">
                  <th className="py-3 px-4 w-12">#</th>
                  <th className="py-3">Title</th>
                  <th className="py-3 hidden md:table-cell">Artist</th>
                  <th className="py-3 hidden lg:table-cell">Album</th>
                  <th className="py-3 px-4 text-right">
                    <Clock size={16} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {tracks.map((track, index) => {
                  const isCurrentTrack = currentTrackIndex === index
                  return (
                    <tr
                      key={track.id}
                      className={`group hover:bg-dark-700/50 cursor-pointer ${isCurrentTrack ? 'bg-dark-700/30' : ''}`}
                      onClick={() => playTrack(index)}
                    >
                      <td className="py-3 px-4">
                        {isCurrentTrack ? (
                          <span className="text-primary-400">
                            {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                          </span>
                        ) : (
                          <>
                            <span className="text-dark-400 group-hover:hidden">{index + 1}</span>
                            <Play size={16} className="text-dark-100 hidden group-hover:block" />
                          </>
                        )}
                      </td>
                      <td className={`py-3 ${isCurrentTrack ? 'text-primary-400' : ''}`}>
                        <div className="font-medium truncate max-w-[200px] md:max-w-none">{track.title}</div>
                        <div className="text-dark-400 text-sm md:hidden truncate">{track.artist_name || 'Unknown'}</div>
                      </td>
                      <td className={`py-3 hidden md:table-cell ${isCurrentTrack ? 'text-primary-400/70' : 'text-dark-400'}`}>
                        {track.artist_name || 'Unknown'}
                      </td>
                      <td className={`py-3 hidden lg:table-cell ${isCurrentTrack ? 'text-primary-400/70' : 'text-dark-400'}`}>
                        {track.album_title || 'Unknown'}
                      </td>
                      <td className={`py-3 px-4 text-right ${isCurrentTrack ? 'text-primary-400/70' : 'text-dark-400'}`}>
                        {formatDuration(track.duration_ms)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-dark-800 rounded-lg text-center py-12">
            <Music size={48} className="mx-auto text-dark-500 mb-4" />
            <p className="text-dark-400">This playlist is empty</p>
          </div>
        )}
      </div>

      {/* Fixed player bar at bottom */}
      {currentTrack && (
        <div className="fixed bottom-0 left-0 right-0 bg-dark-800 border-t border-dark-700 p-4">
          <div className="max-w-4xl mx-auto flex items-center gap-4">
            {/* Track info */}
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{currentTrack.title}</p>
              <p className="text-dark-400 text-sm truncate">{currentTrack.artist_name || 'Unknown'}</p>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={playPrevious}
                disabled={currentTrackIndex === 0}
                className="p-2 text-dark-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <SkipBack size={20} />
              </button>
              <button
                onClick={togglePlayPause}
                className="w-10 h-10 bg-primary-500 hover:bg-primary-600 rounded-full flex items-center justify-center"
              >
                {isPlaying ? (
                  <Pause size={20} fill="white" />
                ) : (
                  <Play size={20} fill="white" className="ml-0.5" />
                )}
              </button>
              <button
                onClick={playNext}
                disabled={currentTrackIndex === tracks.length - 1}
                className="p-2 text-dark-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <SkipForward size={20} />
              </button>
            </div>

            {/* Progress */}
            <div className="hidden md:flex items-center gap-2 flex-1">
              <span className="text-xs text-dark-400 w-10 text-right">{formatTime(currentTime)}</span>
              <input
                type="range"
                min={0}
                max={duration || 100}
                value={currentTime}
                onChange={handleSeek}
                className="flex-1 h-1 bg-dark-600 rounded-lg appearance-none cursor-pointer accent-primary-500"
              />
              <span className="text-xs text-dark-400 w-10">{formatTime(duration)}</span>
            </div>

            {/* Volume */}
            <div className="hidden lg:flex items-center gap-2">
              <button onClick={toggleMute} className="text-dark-400 hover:text-white">
                {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="w-20 h-1 bg-dark-600 rounded-lg appearance-none cursor-pointer accent-primary-500"
              />
            </div>
          </div>
        </div>
      )}

      {/* Hidden Audio Element */}
      {currentTrack && (
        <audio
          ref={audioRef}
          src={getTrackStreamUrl(currentTrack)}
          preload="metadata"
        />
      )}

      {/* Branding */}
      <div className={`text-center py-8 ${currentTrack ? 'pb-24' : ''}`}>
        <p className="text-dark-500 text-xs">Powered by S3ND Media</p>
      </div>
    </div>
  )
}
